import { Injectable, inject, NgZone } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { Environment } from '@micro-core/environment';
import { DevMenuService } from '../dev-menu.service';

/** Mock success response shape expected by BsApiService / GenericResponseResult. */
const MOCK_CARD_ORDER_SUCCESS = {
  result: {
    status: 200,
    code: 'SERVICE_OK',
    message: '',
    suggestion: '',
    suggestionCopyKey: '',
    docs: '',
  },
};

/**
 * When "Simulate no card" or "Simulate isDigitalOnly" is enabled in the dev menu,
 * intercepts card order API requests and returns mock success so the order flows
 * can be tested without live endpoints. In production, always passes through.
 */
@Injectable()
export class CardOrderMockInterceptor implements HttpInterceptor {
  private readonly _env = inject(Environment);
  private readonly _devMenuService = inject(DevMenuService);
  private readonly _ngZone = inject(NgZone);

  private static readonly MOCK_DELAY_MS = 100;

  private _shouldMockCardOrder(): boolean {
    return this._devMenuService.simulateNoCard() || this._devMenuService.simulateIsDigitalOnly();
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this._env.production) {
      return next.handle(req);
    }
    if (!this._shouldMockCardOrder()) {
      return next.handle(req);
    }

    const url = req.url;
    const isPost = req.method === 'POST';

    const isOrderPhysical = isPost && url.includes('/accounts/card/order-physical');
    const body = req.body as { accountId?: string; cardId?: string } | null | undefined;
    const isOrderDigital =
      isPost &&
      url.includes('/accounts/card') &&
      !url.includes('/accounts/card/reissue') &&
      !url.includes('/accounts/card/order-physical') &&
      body != null &&
      typeof body === 'object' &&
      'accountId' in body;

    if (!isOrderPhysical && !isOrderDigital) {
      return next.handle(req);
    }

    const response = new HttpResponse({ status: 200, body: MOCK_CARD_ORDER_SUCCESS });

    return new Observable<HttpEvent<unknown>>((subscriber) => {
      let id: ReturnType<typeof setTimeout>;
      this._ngZone.runOutsideAngular(() => {
        id = setTimeout(() => {
          this._ngZone.run(() => {
            subscriber.next(response);
            subscriber.complete();
          });
        }, CardOrderMockInterceptor.MOCK_DELAY_MS);
      });
      return () => clearTimeout(id!);
    });
  }
}
